import { useKeycloak } from '@react-keycloak/web';
import { createContext, useEffect, useState } from 'react';
import { getCurrentProfileAction } from 'shared/services/user-profile/actions';
import { AppLoader } from 'shared/UI/UIKit/Loader';
import { noop } from 'shared/helpers/utils/noop';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import { ERROR_CODE_SEARCH_PARAM, getAuthorizationUrl } from 'services/navigation';
import type { PropsWithChildren } from 'shared/types';

type AuthContextType = {
  login: () => void;
  logout: () => void;
  authenticated: boolean;
};

export const AuthContext = createContext<AuthContextType>({
  login: noop,
  logout: noop,
  authenticated: false,
});

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const { keycloak } = useKeycloak();
  const dispatch = useAppDispatch();
  const shouldFetchProfile = !!keycloak.authenticated;
  const [isProfileFetching, setIsProfileFetching] = useState(shouldFetchProfile);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsProfileFetching(true);
        await dispatch(getCurrentProfileAction()).unwrap();
      } catch (error) {
        console.log(
          "Exception while get current profile. Redirecting to authorization with 'access_denied' error",
        );
        keycloak.logout({
          redirectUri: `${
            window.location.origin
          }${getAuthorizationUrl()}?${ERROR_CODE_SEARCH_PARAM}=access_denied`,
        });
      } finally {
        setIsProfileFetching(false);
      }
    };

    if (shouldFetchProfile) {
      fetchProfile();
    }
  }, [shouldFetchProfile]);

  return (
    <AuthContext.Provider
      value={{
        login: () => keycloak.login({ redirectUri: window.location.href }),
        logout: () => keycloak.logout(),
        authenticated: !!keycloak.authenticated,
      }}
    >
      {isProfileFetching ? <AppLoader size="large" stretched /> : children}
    </AuthContext.Provider>
  );
};
